import { useState } from 'react';
import { useWidget } from '../WidgetProvider';
import { getRoomTypes, getProjectTypes } from '../config/roomOptions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { RoomType, ProjectTypeConfig } from '@/types/firestore';

export function RoomTypeStep() {
  const { state, setRoomType, setProjectTypes, nextStep, trackEvent, tenantConfig } = useWidget();
  const [projectTypeDialogOpen, setProjectTypeDialogOpen] = useState(false);
  const [pendingRoomType, setPendingRoomType] = useState<RoomType | null>(null);
  const [availableProjectTypes, setAvailableProjectTypes] = useState<ProjectTypeConfig[]>([]);
  const [selectedProjectType, setSelectedProjectType] = useState<string | null>(null);

  // Get room types based on tenant config
  const roomTypes = getRoomTypes(tenantConfig?.widget?.room_config);

  const handleSelect = (roomType: RoomType) => {
    // Check if this room type has project types
    const projectTypes = getProjectTypes(roomType, tenantConfig?.widget?.room_config);

    if (projectTypes && projectTypes.length > 0) {
      // Show project type selection dialog
      setPendingRoomType(roomType);
      setAvailableProjectTypes(projectTypes);
      setSelectedProjectType(null); // Reset selection
      setProjectTypeDialogOpen(true);
    } else {
      // No project types, proceed directly
      setRoomType(roomType);
      setProjectTypes([]);
      trackEvent('room_selected', { room_type: roomType });
      nextStep();
    }
  };

  const handleContinue = () => {
    if (pendingRoomType && selectedProjectType) {
      setRoomType(pendingRoomType);
      setProjectTypes([selectedProjectType]); // Single selection as array for compatibility
      trackEvent('room_selected', {
        room_type: pendingRoomType,
        project_type: selectedProjectType,
      });
      setProjectTypeDialogOpen(false);
      nextStep();
    }
  };

  const handleSkip = () => {
    if (pendingRoomType) {
      setRoomType(pendingRoomType);
      setProjectTypes([]);
      trackEvent('room_selected', { room_type: pendingRoomType, skipped_project_type: true });
      setProjectTypeDialogOpen(false);
      nextStep();
    }
  };

  // Get dynamic dialog title based on selected area
  const getDialogTitle = () => {
    if (!pendingRoomType) return 'What type of project?';
    const room = roomTypes.find((r) => r.id === pendingRoomType);
    return `What type of ${room?.label.toLowerCase() || 'area'} project?`;
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-medium">What area are you renovating?</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Select the type of area you want to transform
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {roomTypes.map((room) => {
          const Icon = room.icon;
          const isSelected = state.roomType === room.id;

          return (
            <Card
              key={room.id}
              className={cn(
                'p-4 cursor-pointer transition-all hover:border-primary/50 hover:shadow-md',
                isSelected && 'border-primary ring-2 ring-primary/20'
              )}
              onClick={() => handleSelect(room.id)}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center',
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium text-sm">{room.label}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {room.description}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Single-Select Project Type Dialog */}
      <Dialog open={projectTypeDialogOpen} onOpenChange={setProjectTypeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>
              Select the type of project you're planning
            </DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={selectedProjectType || ''}
            onValueChange={setSelectedProjectType}
            className="grid gap-2 py-4 max-h-[50vh] overflow-y-auto"
          >
            {availableProjectTypes.map((projectType) => {
              const isSelected = selectedProjectType === projectType.id;
              return (
                <Label
                  key={projectType.id}
                  htmlFor={`project-type-${projectType.id}`}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                    'hover:bg-muted/50 hover:border-primary/50',
                    isSelected && 'bg-primary/5 border-primary'
                  )}
                >
                  <RadioGroupItem
                    value={projectType.id}
                    id={`project-type-${projectType.id}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{projectType.label}</p>
                    {projectType.description && (
                      <p className="text-xs text-muted-foreground">
                        {projectType.description}
                      </p>
                    )}
                  </div>
                </Label>
              );
            })}
          </RadioGroup>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <Button variant="ghost" onClick={handleSkip}>
              Skip
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!selectedProjectType}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
